import {
  createRouter,
  createWebHistory,
  isNavigationFailure,
  LocationQueryValue,
  NavigationFailureType,
  RouteLocationNormalized,
  RouteRecordRaw,
} from 'vue-router';

import { notNull } from '@rygen/rygen-ui/util';
import AuthCallback from '@rygen/rygen-ui/views/AuthCallback.vue';
import Error401View from '@rygen/rygen-ui/views/Error401View.vue';
import Error404View from '@rygen/rygen-ui/views/Error404View.vue';

import { useAbortControllerStore } from '@/stores/AbortController';
import AiOrderEntry from '@/views/AiOrderEntry.vue';
import AiOrderEntryDraftList from '@/views/AiOrderEntryDraftList.vue';
import CarrierPortalShipmentDetail from '@/views/carrierPortal/CarrierPortalShipmentDetail.vue';
import CarrierPortalSpotMarket from '@/views/carrierPortal/CarrierPortalSpotMarket.vue';
import Dashboard from '@/views/Dashboard.vue';
import DomainPopulator from '@/views/domainpopulator/DomainPopulator.vue';
import ManifestDetail from '@/views/ManifestDetail.vue';
import ManifestList from '@/views/ManifestList.vue';
import OrderDetail from '@/views/OrderDetail.vue';
import OrderEntry from '@/views/OrderEntry.vue';
import OrderImport from '@/views/OrderImport.vue';
import OrderList from '@/views/OrderList.vue';
import QuickQuote from '@/views/QuickQuote.vue';
import RouteBuilderContainer from '@/views/routeBuilder/RouteBuilderContainer.vue';
import RouteBuilderPhase1 from '@/views/routeBuilder/RouteBuilderPhase1.vue';
import RouteBuilderPhase2 from '@/views/routeBuilder/RouteBuilderPhase2.vue';
import RouteBuilderPopOutMap from '@/views/routeBuilder/RouteBuilderPopOutMap.vue';
import RoutePlanList from '@/views/routeBuilder/RoutePlanList.vue';
import ShipmentDetail from '@/views/ShipmentDetail.vue';
import ShipmentEntry from '@/views/ShipmentEntry.vue';
import ShipmentList from '@/views/ShipmentList.vue';
import ShipOrderConsolidation from '@/views/ShipOrderConsolidation.vue';
import UnauthenticatedTrackAndTrace from '@/views/UnauthenticatedTrackAndTrace.vue';

import RequireAuthnNavGuard from './navigationGuard/RequireAuthnNavGuard';
import RequireDomainContextNavGuard from './navigationGuard/RequireDomainContextNavGuard';
import RequireHardRefreshNavGuard from './navigationGuard/RequireHardRefreshNavGuard';
import RequirePermissionNavGuard from './navigationGuard/RequirePermissionNavGuard';
import UpdateTitleNavGuard from './navigationGuard/UpdateTitleNavGuard';

const parseIdParam = (
  possibleId: LocationQueryValue | LocationQueryValue[],
) => {
  if (Array.isArray(possibleId)) {
    return possibleId.filter(notNull).map((idStr) => parseInt(idStr, 10));
  } else if (typeof possibleId === 'string') {
    return [parseInt(possibleId, 10)];
  } else {
    return undefined;
  }
};

const routes: Array<RouteRecordRaw> = [
  {
    name: '401',
    path: '/unauthorized',
    component: Error401View,
    meta: {
      requiresDomainContext: false,
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: Dashboard,
    meta: {
      requiredAuthorization: [
        ['order', 'read'],
        ['shipment', 'read'],
      ],
    },
  },
  {
    path: '/',
    name: 'home',
    redirect: '/dashboard',
  },
  {
    path: '/auth/callback',
    name: 'authCallback',
    component: AuthCallback,
    meta: {
      requiresAuthn: false,
      requiresDomainContext: false,
    },
  },
  {
    path: '/order',
    redirect: '/order/list',
  },
  {
    path: '/order/list',
    name: 'orderList',
    component: OrderList,
    meta: {
      title: 'Orders',
      requiredAuthorization: [['order', 'read']],
    },
  },
  {
    path: '/order/entry',
    name: 'orderEntry',
    component: OrderEntry,
    meta: {
      title: 'New Order',
      requiredAuthorization: [['order', 'create']],
    },
  },
  {
    path: '/order/ai-entry',
    name: 'aiOrderEntry',
    component: AiOrderEntry,
    meta: {
      title: 'AI Order Entry',
      requiredAuthorization: [['order', 'create']],
    },
  },
  {
    path: '/order/drafts',
    name: 'aiOrderEntryDraftList',
    component: AiOrderEntryDraftList,
    meta: {
      title: 'Draft Orders',
      requiredAuthorization: [['order', 'create']],
    },
  },
  {
    path: '/order/consolidate/:orderId',
    name: 'orderConsolidation',
    redirect: (to) => {
      return {
        name: 'shipOrderConsolidation',
        query: {
          orderId: to.params.orderId,
        },
      };
    },
  },
  {
    path: '/order/:orderId',
    name: 'orderDetail',
    component: OrderDetail,
    props: (route) => {
      if (typeof route?.params?.orderId === 'string') {
        return {
          orderId: parseInt(route.params.orderId),
        };
      }

      return {};
    },
    meta: {
      title: (route: RouteLocationNormalized) =>
        `Order ${route?.params?.orderId}`,
      requiredAuthorization: [['order', 'read']],
      redirectOnDomainChange: true,
    },
  },

  {
    path: '/order/import',
    name: 'importOrders',
    component: OrderImport,
    meta: {
      requiredAuthorization: [['order', 'create']],
      title: 'Import Orders',
    },
  },
  {
    path: '/order/import/job/:jobId',
    name: 'importOrdersJobStatus',
    component: OrderImport,
    meta: {
      requiredAuthorization: [['order', 'create']],
      title: (route: RouteLocationNormalized) =>
        `Order Import Job ${route?.params?.jobId}`,
    },
    props: (r) => {
      if (typeof r?.params?.jobId === 'string') {
        return {
          jobId: parseInt(r.params.jobId, 10),
        };
      }
      return r;
    },
  },
  { path: '/shipment', redirect: '/shipment/list' },
  {
    path: '/shipment/list',
    name: 'shipmentList',
    component: ShipmentList,
    meta: {
      title: 'Shipments',
      requiredAuthorization: [['shipment', 'read']],
    },
  },
  { path: '/manifest', redirect: '/manifest/list' },
  {
    path: '/manifest/list',
    name: 'manifestList',
    component: ManifestList,
    meta: {
      title: 'Manifests',
      requiredAuthorization: [['shipment', 'read']],
    },
  },
  {
    path: '/manifest/:manifestId',
    name: 'manifestDetail',
    component: ManifestDetail,
    meta: {
      title: 'Manifest',
      requiredAuthorization: [['shipment', 'read']],
    },
  },
  {
    path: '/shipment/entry',
    name: 'shipmentEntry',
    component: ShipmentEntry,
    meta: {
      title: 'New Shipment',
      requiredAuthorization: [['shipment', 'manualEntry']],
    },
  },
  {
    path: '/shipment/:shipmentId',
    name: 'shipmentDetail',
    component: ShipmentDetail,
    props: (route) => {
      if (typeof route?.params?.shipmentId === 'string') {
        return {
          shipmentId: parseInt(route.params.shipmentId, 10),
        };
      }

      throw new Error('Id is required for Shipment route');
    },
    meta: {
      title: (route: RouteLocationNormalized) =>
        `Shipment ${route?.params?.shipmentId}`,
      requiredAuthorization: [['shipment', 'read']],
      redirectOnDomainChange: true,
    },
  },
  {
    path: '/carrier-portal/shipment/:shipmentId',
    name: 'carrierPortalShipmentDetail',
    component: CarrierPortalShipmentDetail,
    props: (route) => {
      if (!route?.query?.accessToken) {
        throw new Error('accessToken is required for this route');
      }

      if (typeof route.params?.shipmentId === 'string') {
        return {
          shipmentId: parseInt(route.params.shipmentId, 10),
          accessToken: route.query.accessToken,
          checkCallAction: route.query.checkCallAction,
          tenderResponse: route.query.tenderResponse,
          sentFromRequiredDocumentsEmail:
            route.query.sentFromRequiredDocumentsEmail === 'true',
        };
      }

      throw new Error('Id is required for Shipment route');
    },
    meta: {
      requiresDomainContext: false,
      requiresAuthn: false,
      title: (route: RouteLocationNormalized) =>
        `Shipment ${route.params?.shipmentId}`,
    },
  },
  {
    path: '/shipment/:shipmentId/tender/:tenderResponse',
    redirect: (to) => {
      return {
        name: 'carrierPortalShipmentDetail',
        param: {
          shipmentId: to.params.shipmentId,
        },
        query: {
          accessToken: to.query.accessToken,
          tenderResponse: to.params.tenderResponse,
        },
      };
    },
  },
  {
    path: '/carrier-portal/order/:orderId/spot-market',
    name: 'carrierPortalOrderSpotMarket',
    component: CarrierPortalSpotMarket,
    props: (route) => {
      if (!route?.query?.accessToken) {
        throw new Error('accessToken is required for this route');
      }

      if (typeof route.params?.orderId !== 'string') {
        throw new Error('Id is required for Order route');
      }
      if (typeof route.query?.shipmentSpecId !== 'string') {
        throw new Error('shipmentSpecId is required for this route');
      }

      return {
        orderId: parseInt(route.params.orderId, 10),
        shipmentSpecId: parseInt(route.query.shipmentSpecId, 10),
        action: route.query.action,
        accessToken: route.query.accessToken,
      };
    },
    meta: {
      requiresDomainContext: false,
      requiresAuthn: false,
      title: (route: RouteLocationNormalized) =>
        `Order ${route.params?.orderId} Spot Market`,
    },
  },
  {
    path: '/carrier-portal/shipment/:shipmentId/spot-market',
    name: 'carrierPortalShipmentSpotMarket',
    component: CarrierPortalSpotMarket,
    props: (route) => {
      if (!route?.query?.accessToken) {
        throw new Error('accessToken is required for this route');
      }

      if (typeof route.params?.shipmentId === 'string') {
        return {
          shipmentId: parseInt(route.params.shipmentId, 10),
          accessToken: route.query.accessToken,
          action: route.query.action,
        };
      }

      throw new Error('Id is required for Shipment route');
    },
    meta: {
      requiresDomainContext: false,
      requiresAuthn: false,
      title: (route: RouteLocationNormalized) =>
        `Shipment ${route.params?.shipmentId} Spot Market`,
    },
  },
  {
    path: '/order/:orderId/tracking',
    name: 'orderTracking',
    component: UnauthenticatedTrackAndTrace,
    props: (route) => {
      if (typeof route?.params?.orderId === 'string') {
        return {
          orderId: parseInt(route.params.orderId, 10),
          accessToken: route.query.accessToken,
        };
      }

      throw new Error('Id is required for Order route');
    },
    meta: {
      requiresDomainContext: false,
      requiresAuthn: false,
      contentOnly: true,
      title: (route: RouteLocationNormalized) =>
        `Order ${route.params?.orderId} Tracking`,
    },
  },
  {
    path: '/shipment-planning/consolidation',
    name: 'shipOrderConsolidation',
    component: ShipOrderConsolidation,
    props: (route) => {
      const orderId =
        typeof route?.query?.orderId === 'string'
          ? parseInt(route.query.orderId, 10)
          : undefined;
      const shipmentId =
        typeof route?.query?.shipmentId === 'string'
          ? parseInt(route.query.shipmentId, 10)
          : undefined;

      if (orderId && shipmentId) {
        throw new Error(
          "Only one of 'orderId' or 'shipmentId' should be present",
        );
      }

      return { orderId, shipmentId };
    },
    meta: {
      title: 'New Shipment',
      requiredAuthorization: [['shipment', 'consolidate']],
      redirectOnDomainChange: true,
    },
  },
  {
    path: '/route-builder',
    name: 'routeBuilder',
    component: RouteBuilderContainer,
    children: [
      {
        path: 'v1',
        name: 'routeBuilderPhase1',
        component: RouteBuilderPhase1,
        props: (route) => {
          const orderIds = parseIdParam(route?.query?.orderId);
          const shipmentIds = parseIdParam(route?.query?.shipmentId);

          if (orderIds?.length && shipmentIds?.length) {
            throw new Error(
              "Only one of 'orderId' or 'shipmentId' should be present",
            );
          }

          return {
            ...route.query,
            orderIds,
            shipmentIds,
          };
        },
        meta: {
          title: 'Route Builder',
          requiredAuthorization: [['shipment', 'consolidate']],
          redirectOnDomainChange: true,
        },
      },
      {
        path: 'v2',
        name: 'routeBuilderPhase2',
        component: RouteBuilderPhase2,
        props: (route) => {
          const orderIds = parseIdParam(route?.query?.orderId);
          const shipmentIds = parseIdParam(route?.query?.shipmentId);
          const routePlanId = parseIdParam(route?.query?.routePlanId)?.[0];

          if (orderIds?.length && shipmentIds?.length) {
            throw new Error(
              "Only one of 'orderId' or 'shipmentId' should be present",
            );
          }

          return {
            ...route.query,
            orderIds,
            shipmentIds,
            routePlanId,
          };
        },
        meta: {
          title: 'Route Builder',
          requiredAuthorization: [['shipment', 'consolidate']],
          redirectOnDomainChange: true,
        },
      },
    ],
  },
  {
    // Flag-gated inside RoutePlanList (app convention: components, not the
    // router), on route-builder-transport-segment-feat-flag.
    path: '/route-builder/plans',
    name: 'routePlanList',
    component: RoutePlanList,
    meta: {
      title: 'Route Plans',
      requiredAuthorization: [['shipment', 'consolidate']],
      redirectOnDomainChange: true,
    },
  },
  {
    path: '/route-builder/pop-out-map',
    name: 'routeBuilderPopOutMap',
    component: RouteBuilderPopOutMap,
    meta: {
      title: 'Route Builder Pop Out Map',
      contentOnly: true,
    },
  },
  {
    path: '/quick-quote',
    name: 'quickQuote',
    component: QuickQuote,
    meta: {
      requiresAuthn: false,
      title: (_route: RouteLocationNormalized) => `Quick Quote`,
    },
  },
  {
    path: '/domain-populator',
    name: 'domainPopulator',
    component: DomainPopulator,
    meta: {
      title: 'Domain Populator',
      requiredAuthorization: [['domainPopulator', 'invoke']],
    },
  },
  {
    // catch-all route matcher must always be the very last route in this list
    path: '/:pathMatch(.*)*',
    name: 'notFound',
    component: Error404View,
    meta: {
      requiresAuthn: false,
      requiresDomainContext: false,
    },
  },
];

const routeMetaDefaults = {
  contentOnly: false,
  requiresDomainContext: true,
  requiresAuthn: true,
};

// apply default route.meta
routes.forEach((route) => {
  if (route.meta === undefined) {
    route.meta = { ...routeMetaDefaults };
  } else {
    route.meta = { ...routeMetaDefaults, ...route.meta };
  }
});

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
        top: 100,
      };
    } else if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
});

router.beforeEach(RequireHardRefreshNavGuard);
router.beforeEach(RequireAuthnNavGuard);
router.beforeEach(RequireDomainContextNavGuard);
router.beforeEach(RequirePermissionNavGuard);

router.afterEach((_to, from, failure) => {
  if (isNavigationFailure(failure, NavigationFailureType.duplicated)) {
    return;
  }

  const { cancelRequests } = useAbortControllerStore();

  try {
    cancelRequests(from, 'nav guard');
  } catch (e) {
    // TODO: Send this to stackdriver
    console.warn('Failed to cancel previous routes axios requests', e);
  }
});
router.afterEach(UpdateTitleNavGuard);

export default router;
